/**
 * src/admin-ui/src/store/authStore.ts
 *
 * In-memory authentication state backed by the native Keycloak session.
 */
import { create } from 'zustand';

import { AuthState, User } from '../types/auth';
import {
  getAuthenticatedUser,
  initKeycloak,
  keycloak,
  login as keycloakLogin,
  logout as keycloakLogout,
  register as keycloakRegister,
} from '../utils/keycloak';

interface AuthStore extends AuthState {
  initialize: () => Promise<void>;
  login: (redirectPath?: string) => Promise<void>;
  register: () => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

let callbacksConfigured = false;

export const useAuthStore = create<AuthStore>((set) => {
  const synchronizeSession = () => {
    const user = getAuthenticatedUser();
    set({
      isAuthenticated: user !== null,
      user,
      loading: false,
      error: null,
    });
  };

  return {
    isAuthenticated: false,
    user: null,
    loading: true,
    error: null,

    initialize: async () => {
      set({ loading: true, error: null });
      try {
        if (!callbacksConfigured) {
          keycloak.onAuthSuccess = synchronizeSession;
          keycloak.onAuthRefreshSuccess = synchronizeSession;
          keycloak.onAuthLogout = () => {
            set({ isAuthenticated: false, user: null, loading: false, error: null });
          };
          keycloak.onTokenExpired = () => {
            void keycloak
              .updateToken(30)
              .then(synchronizeSession)
              .catch(() => {
                set({
                  isAuthenticated: false,
                  user: null,
                  loading: false,
                  error: 'Сессия истекла. Войдите снова.',
                });
              });
          };
          callbacksConfigured = true;
        }

        await initKeycloak();
        synchronizeSession();
      } catch {
        set({
          isAuthenticated: false,
          user: null,
          loading: false,
          error: 'Не удалось подключиться к серверу аутентификации.',
        });
      }
    },

    login: async (redirectPath = '/dashboard') => {
      set({ loading: true, error: null });
      await keycloakLogin(redirectPath);
    },

    register: async () => {
      set({ loading: true, error: null });
      await keycloakRegister();
    },

    logout: async () => {
      set({ loading: true, error: null });
      await keycloakLogout();
    },

    setUser: (user) => {
      set({
        user,
        isAuthenticated: user !== null,
        loading: false,
        error: null,
      });
    },

    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
  };
});
